import * as React from 'react'

import { Box, Button, Divider, Table, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { OverflowContextProvider } from 'modules/core/lib/overflowContext'

import CompetencyInfo from 'modules/competencies/components/CompetencyInfo'
import type { Assessment } from 'modules/competencies/store/models/Assessment'
import type { Competency } from 'modules/competencies/store/models/Competency'

import Comment from './Comment'

interface CompetencyProps {
  assessment: Assessment
}

const CompetencyComment = ({ assessment }: CompetencyProps) => {
  const [commentModalOpen, toggleCommentModal] = useToggle(false)

  return (
    <Box
      flexDirection="row"
      aria-label="assessment-competency-comment"
      style={{ boxSizing: 'border-box' }}
    >
      <Button
        type="secondary"
        onClick={toggleCommentModal(true)}
        iconName={assessment.attributes.description ? 'file-filled' : 'file'}
      ></Button>
      {commentModalOpen && <Comment assessment={assessment} onClose={toggleCommentModal(false)} />}
    </Box>
  )
}

const CompetencyRating = ({ assessment }: CompetencyProps) => {
  const { options } = assessment.competency

  return (
    <Box flexDirection="row" alignItems="center" flexGrow aria-label="assessment-competency-rating">
      {options.map((option) => {
        const { id, value } = option.attributes
        const isSelected = assessment?.option?.attributes.id === id

        return (
          <Box
            key={id}
            width="s40"
            height="s32"
            justifyContent="center"
            alignItems="center"
            borderRadius={{ all: 'abs008' }}
            background={isSelected ? 'grey800' : 'lighter000'}
          >
            <Text.Normal size="200" color={isSelected ? 'grey000White' : 'grey800'}>
              {value}
            </Text.Normal>
          </Box>
        )
      })}
    </Box>
  )
}

interface CompetencyRowProps {
  assessment?: Assessment
  competency: Competency
  isParent?: boolean
}

const CompetencyRow = observer(
  ({ assessment, competency, isParent = false }: CompetencyRowProps) => {
    const ref = React.useRef(null)

    return (
      <Table.Row background={isParent ? 'grey000White' : 'grey100'}>
        <Box flexDirection="row" justifyContent="spaceBetween" gap="s32" padding="s16">
          <Box
            flexDirection="row"
            alignItems="center"
            width="s300"
            gap="s12"
            aria-label="assessment-competency-name"
          >
            <OverflowContextProvider childrenRef={ref} value={competency.attributes.name}>
              <Text.Normal weight="medium" innerRef={ref} ellipsis>
                {competency.attributes.name}
              </Text.Normal>
            </OverflowContextProvider>
            <CompetencyInfo competency={competency} />
          </Box>
          {assessment && <CompetencyRating assessment={assessment} />}
          {assessment && <CompetencyComment assessment={assessment} />}
        </Box>
      </Table.Row>
    )
  }
)

interface Props {
  parentAssessment?: Assessment
  childAssessments: Assessment[]
}

const CompetenciesTable = ({ parentAssessment, childAssessments }: Props) => {
  const parentCompetency =
    parentAssessment?.competency || childAssessments.at(0)?.competency?.parent

  if (!parentCompetency) {
    throw new Error(
      `No parent competency found! First child id: ${childAssessments.at(0)?.competency.id}`
    )
  }

  return (
    <>
      <Divider />
      <CompetencyRow assessment={parentAssessment} competency={parentCompetency} isParent />
      {childAssessments.map((assessment) => (
        <>
          <Divider />
          <CompetencyRow
            key={assessment.attributes.id}
            assessment={assessment}
            competency={assessment.competency}
          />
        </>
      ))}
    </>
  )
}

export default observer(CompetenciesTable)
